import { useEffect, useState } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import api, { money, shortDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, Button, Input, Select, Modal, PageHeader, Badge } from "@/components/kit";
import { CaretLeft, Plus, Lightning, ArrowsClockwise } from "@phosphor-icons/react";

export default function SavingsDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [acc, setAcc] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "deposit", amount: "", note: "" });
  const [err, setErr] = useState("");
  const [renewOpen, setRenewOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeForm, setCloseForm] = useState({ closure_type: "premature", penalty_pct: "", penalty_flat: "", note: "" });
  const [defaultPenalty, setDefaultPenalty] = useState(1);
  const [closeErr, setCloseErr] = useState("");
  const [closing, setClosing] = useState(false);
  const [loanReqOpen, setLoanReqOpen] = useState(false);
  const [loanReqForm, setLoanReqForm] = useState({ amount: "", tenure_days: 120, interest_rate: 12, interest_method: "SI", purpose: "", notes: "" });
  const [loanReqErr, setLoanReqErr] = useState("");
  const [loanReqSaving, setLoanReqSaving] = useState(false);
  const [loanReqDone, setLoanReqDone] = useState(false);
  const [linkedLoans, setLinkedLoans] = useState([]);
  const [settlePick, setSettlePick] = useState({}); // {loan_id: bool}

  const load = async () => {
    const a = await api.get(`/savings/${id}`);
    setAcc(a.data);
    const [l, c] = await Promise.all([
      api.get(`/savings/${id}/ledger`),
      api.get(`/customers/${a.data.customer_id}`).catch(() => ({ data: null })),
    ]);
    setLedger(l.data);
    setCustomer(c.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    api.get("/settings/penalties").then(({ data }) => {
      setDefaultPenalty(data?.default_premature_penalty_pct ?? 1);
    }).catch(() => {});
  }, []);

  const openCloseModal = async () => {
    // Default type: mature if past maturity_date, else premature
    const today = new Date().toISOString().slice(0, 10);
    const isMatured = acc.maturity_date && acc.maturity_date <= today;
    setCloseForm({
      closure_type: isMatured ? "mature" : "premature",
      penalty_pct: acc.premature_penalty_pct ?? defaultPenalty ?? 1,
      penalty_flat: "",
      note: "",
    });
    setCloseErr("");
    // Load linked deposit-backed loans; pre-check all so they get settled by default
    try {
      const { data } = await api.get(`/savings/${id}/linked-loans`);
      setLinkedLoans(data || []);
      const pre = {};
      (data || []).forEach((l) => { pre[l.id] = true; });
      setSettlePick(pre);
    } catch {
      setLinkedLoans([]); setSettlePick({});
    }
    setCloseOpen(true);
  };

  const previewClosePenalty = () => {
    if (!acc || closeForm.closure_type !== "premature") return 0;
    if (closeForm.penalty_flat !== "" && closeForm.penalty_flat != null) return Number(closeForm.penalty_flat) || 0;
    const pct = Number(closeForm.penalty_pct) || 0;
    return Math.round(((acc.balance || 0) * pct / 100) * 100) / 100;
  };
  const previewSettleTotal = () => {
    const afterPenalty = (acc?.balance || 0) - previewClosePenalty();
    let remaining = afterPenalty;
    let total = 0;
    for (const l of linkedLoans) {
      if (!settlePick[l.id]) continue;
      const settleAmt = Math.min(l.outstanding || 0, Math.max(0, remaining));
      total += settleAmt;
      remaining -= settleAmt;
    }
    return Math.round(total * 100) / 100;
  };
  const previewClosePayout = () => {
    if (!acc) return 0;
    return Math.round(((acc.balance || 0) - previewClosePenalty() - previewSettleTotal()) * 100) / 100;
  };

  const submitClose = async (e) => {
    e.preventDefault();
    setCloseErr(""); setClosing(true);
    try {
      const body = { closure_type: closeForm.closure_type };
      if (closeForm.closure_type === "premature") {
        if (closeForm.penalty_flat !== "" && closeForm.penalty_flat != null) {
          body.penalty_flat = Number(closeForm.penalty_flat);
        } else if (closeForm.penalty_pct !== "" && closeForm.penalty_pct != null) {
          body.penalty_pct = Number(closeForm.penalty_pct);
        }
      }
      if (closeForm.note) body.note = closeForm.note;
      const settleIds = Object.entries(settlePick).filter(([, v]) => v).map(([k]) => k);
      if (settleIds.length) body.settle_loan_ids = settleIds;
      await api.post(`/savings/${id}/close`, body);
      setCloseOpen(false);
      load();
    } catch (e2) {
      setCloseErr(formatApiError(e2.response?.data?.detail) || "Failed to close account");
    } finally { setClosing(false); }
  };

  const submitTx = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post(`/savings/${id}/transactions`, {
        ...form,
        amount: Number(form.amount),
      });
      setOpen(false);
      setForm({ type: "deposit", amount: "", note: "" });
      load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Failed");
    }
  };

  const accrue = async () => {
    try {
      const { data } = await api.post(`/savings/${id}/accrue-interest`);
      alert(`Credited ${money(data.interest_credited)} interest for ${data.days} days.`);
      load();
    } catch (e2) {
      alert(e2.response?.data?.detail || "Failed");
    }
  };

  const renewNow = async (overrides = {}) => {
    try {
      const { data } = await api.post(`/savings/${id}/renew`, overrides);
      setRenewOpen(false);
      nav(`/savings/${data.id}`);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || "Renew failed");
    }
  };

  if (!acc) return <div className="p-8 text-slate-500">Loading…</div>;

  const canTx = ["super_admin", "branch_admin", "staff"].includes(user.role);
  const canAccrue = ["super_admin", "branch_admin"].includes(user.role) && acc.product === "daily";
  const canRenew = ["super_admin", "branch_admin", "staff"].includes(user.role) &&
    (acc.product === "fd" || acc.product === "rd") && acc.status !== "matured";
  const today = new Date().toISOString().slice(0, 10);
  const isMature = acc.maturity_date && acc.maturity_date <= today;

  return (
    <div className="w-full p-4 md:p-8 pb-24 md:pb-8 fade-up">
      <Link to="/savings" className="text-sm text-slate-500 inline-flex items-center gap-1 mb-4 hover:text-slate-800 print:hidden">
        <CaretLeft size={14} /> Back to savings
      </Link>

      <div className="print-header">
        <div className="text-lg font-bold text-emerald-800">Ujjwal Multipurpose Cooperative Society Ltd</div>
        <div className="text-sm text-slate-600">Passbook / Account Statement</div>
      </div>

      <PageHeader
        title={<>{productLabel(acc.product)} <span className="text-slate-400 text-lg mono ml-2">{acc.account_no || `#${acc.id.slice(0, 8)}`}</span></>}
        subtitle={customer && <Link to={`/customers/${customer.id}`} className="text-emerald-700 hover:underline">{customer.name} · <span className="mono">{customer.phone}</span></Link>}
        action={
          <div className="flex gap-2">
            {canAccrue && acc.status === "active" && (
              <Button variant="secondary" onClick={accrue} data-testid="accrue-interest-btn">
                <Lightning size={16} /> Accrue interest
              </Button>
            )}
            {canTx && acc.status === "active" && (
              <Button onClick={() => setOpen(true)} data-testid="add-transaction-btn">
                <Plus size={16} /> New transaction
              </Button>
            )}
            {canTx && acc.status === "active" && (
              <button
                type="button"
                onClick={openCloseModal}
                className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium border border-rose-200 text-rose-700 hover:bg-rose-50"
                data-testid="open-close-savings"
              >
                Close / Withdraw
              </button>
            )}
            {acc.status === "active" && (acc.balance || 0) > 0 && (
              <button
                type="button"
                onClick={() => { setLoanReqOpen(true); setLoanReqDone(false); setLoanReqErr(""); }}
                className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium border border-sky-200 text-sky-700 hover:bg-sky-50"
                data-testid="open-loan-request"
              >
                Request loan against this
              </button>
            )}
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 print:hidden"
              data-testid="print-passbook"
            >
              🖨 Print
            </button>
          </div>
        }
      />

      {acc.status === "matured" && acc.renewed_to && (
        <Card className="p-4 mb-4 border-emerald-200 bg-emerald-50/40" data-testid="matured-banner">
          <div className="text-sm">
            This account was matured and renewed as a new account.
            <Link to={`/savings/${acc.renewed_to}`} className="text-emerald-700 hover:underline font-medium ml-1" data-testid="open-renewed-link">
              Open the renewed account →
            </Link>
          </div>
        </Card>
      )}

      {canRenew && isMature && (
        <Card className="p-4 mb-4 border-amber-200 bg-amber-50/40" data-testid="renew-prompt">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm font-semibold text-amber-900">This deposit has matured</div>
              <div className="text-xs text-slate-600 mt-0.5">
                Matured on {shortDate(acc.maturity_date)} · Renew for another {acc.tenure_months} months at {acc.interest_rate}% ({acc.interest_method}).
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => renewNow()} data-testid="quick-renew-btn">
                <ArrowsClockwise size={14} weight="bold" /> Quick renew
              </Button>
              <Button variant="secondary" onClick={() => setRenewOpen(true)} data-testid="custom-renew-btn">
                Custom renew…
              </Button>
            </div>
          </div>
        </Card>
      )}

      {acc.status === "closed" && (
        <Card className={`p-4 mb-4 ${acc.closure_type === "premature" ? "border-rose-200 bg-rose-50/40" : "border-emerald-200 bg-emerald-50/40"}`} data-testid="savings-closed-banner">
          <div className="text-sm space-y-1">
            <div>
              Account {acc.closure_type === "premature" ? "closed prematurely" : "closed on maturity"} on {shortDate(acc.closed_at)}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mono text-xs">
              <span>Balance <span className="font-semibold">{money((acc.penalty_charged || 0) + (acc.loan_settled_amount || 0) + (acc.payout_amount || 0))}</span></span>
              {acc.penalty_charged > 0 && <span>Penalty <span className="text-rose-700 font-semibold">−{money(acc.penalty_charged)}</span></span>}
              {acc.loan_settled_amount > 0 && <span>Loan settled <span className="text-rose-700 font-semibold">−{money(acc.loan_settled_amount)}</span></span>}
              <span>Payout <span className="text-emerald-700 font-semibold">{money(acc.payout_amount || 0)}</span></span>
            </div>
            {Array.isArray(acc.settled_loan_details) && acc.settled_loan_details.length > 0 && (
              <div className="text-xs text-slate-600 pt-1">
                Loans settled: {acc.settled_loan_details.map((s) => (
                  <span key={s.loan_id} className="mono mr-2">{s.loan_no || s.loan_id.slice(0, 8)}({money(s.settled_amount)})</span>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4"><div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Balance</div><div className="mono heading text-2xl font-semibold text-emerald-700" data-testid="savings-balance">{money(acc.balance)}</div></Card>
        <Card className="p-4"><div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Rate</div><div className="mono heading text-2xl font-semibold">{acc.interest_rate}%</div><div className="text-xs text-slate-500">{acc.interest_method}</div></Card>
        {acc.maturity_value && (
          <Card className="p-4"><div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Maturity</div><div className="mono heading text-2xl font-semibold text-slate-900">{money(acc.maturity_value)}</div></Card>
        )}
        <Card className="p-4"><div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Opened</div><div className="text-sm">{shortDate(acc.opened_on)}</div></Card>
      </div>

      <Card>
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="heading font-semibold">Ledger / Passbook</h3>
        </div>
        {ledger.length === 0 ? (
          <div className="text-sm text-slate-500 p-6 text-center">No transactions yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3">Note</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-right px-4 py-3">Balance</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 text-slate-600">{shortDate(t.created_at)}</td>
                    <td className="px-4 py-3">
                      <Badge tone={t.type === "withdrawal" ? "danger" : t.type === "penalty" ? "warning" : t.type === "interest" ? "info" : "success"}>{t.type}</Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-500">{t.note || "—"}</td>
                    <td className={`px-4 py-3 mono text-right font-medium ${(t.type === "withdrawal" || t.type === "penalty") ? "text-rose-600" : "text-emerald-700"}`}>
                      {(t.type === "withdrawal" || t.type === "penalty") ? "−" : "+"}{money(t.amount)}
                    </td>
                    <td className="px-4 py-3 mono text-right">{money(t.balance_after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title="New transaction">
        <form onSubmit={submitTx} className="space-y-4">
          <Select label="Type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} testId="tx-type">
            <option value="deposit">Deposit</option>
            <option value="withdrawal">Withdrawal</option>
          </Select>
          <Input label="Amount" type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} testId="tx-amount" />
          <Input label="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} testId="tx-note" />
          {err && <div className="text-sm text-rose-600">{String(err)}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="save-tx-btn">Record</Button>
          </div>
        </form>
      </Modal>

      <RenewModal
        open={renewOpen}
        onClose={() => setRenewOpen(false)}
        account={acc}
        onSubmit={renewNow}
      />

      <Modal open={closeOpen} onClose={() => setCloseOpen(false)} title="Close deposit / withdraw" maxWidth="max-w-lg">
        <form onSubmit={submitClose} className="space-y-4">
          <div className="text-sm text-slate-600">
            <div><span className="text-slate-400">Account:</span> <span className="mono">{productLabel(acc.product)} #{acc.id.slice(0, 8)}</span></div>
            <div><span className="text-slate-400">Current balance:</span> <span className="mono font-semibold text-emerald-700">{money(acc.balance || 0)}</span></div>
            {acc.maturity_date && <div><span className="text-slate-400">Maturity:</span> <span className="mono">{shortDate(acc.maturity_date)}</span></div>}
          </div>
          {acc.maturity_date && (
            <div>
              <div className="text-xs font-medium text-slate-600 mb-1.5">Closure type</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  ["mature", "On maturity", "No penalty"],
                  ["premature", "Pre-mature", "Penalty applies"],
                ].map(([v, l, s]) => (
                  <button
                    type="button"
                    key={v}
                    onClick={() => setCloseForm({ ...closeForm, closure_type: v })}
                    data-testid={`sd-closure-type-${v}`}
                    className={`text-left border rounded-lg px-3 py-2 ${
                      closeForm.closure_type === v ? "border-emerald-500 bg-emerald-50" : "border-slate-200"
                    }`}
                  >
                    <div className="text-sm font-semibold">{l}</div>
                    <div className="text-xs text-slate-500">{s}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {closeForm.closure_type === "premature" && (
            <>
              <div className="text-xs text-slate-500 -mb-2">
                Choose a % <em>or</em> a flat rupee amount — the flat value takes priority if both are set.
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Penalty %" type="number" step="0.01" testId="sd-close-penalty-pct"
                  value={closeForm.penalty_pct}
                  onChange={(e) => setCloseForm({ ...closeForm, penalty_pct: e.target.value, penalty_flat: "" })}
                />
                <Input
                  label="…or flat penalty (₹)" type="number" step="0.01" testId="sd-close-penalty-flat"
                  value={closeForm.penalty_flat}
                  onChange={(e) => setCloseForm({ ...closeForm, penalty_flat: e.target.value })}
                />
              </div>
            </>
          )}
          <Input label="Note (optional)" testId="sd-close-note"
            value={closeForm.note}
            onChange={(e) => setCloseForm({ ...closeForm, note: e.target.value })} />

          {linkedLoans.length > 0 && (
            <div className="border border-sky-200 rounded-lg p-3 bg-sky-50/50" data-testid="sd-linked-loans">
              <div className="text-xs font-semibold text-sky-900 mb-2">
                Linked loan(s) against this deposit — settle from balance?
              </div>
              <div className="space-y-1.5">
                {linkedLoans.map((l) => (
                  <label key={l.id} className="flex items-center gap-2 text-sm" data-testid={`sd-settle-${l.id}`}>
                    <input
                      type="checkbox"
                      className="accent-emerald-700"
                      checked={!!settlePick[l.id]}
                      onChange={() => setSettlePick((p) => ({ ...p, [l.id]: !p[l.id] }))}
                      data-testid={`sd-settle-cb-${l.id}`}
                    />
                    <span className="mono text-slate-700">{l.loan_no || l.id.slice(0, 8)}</span>
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-600">outstanding</span>
                    <span className="mono font-semibold text-rose-700">{money(l.outstanding)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Balance</span><span className="mono">{money(acc.balance || 0)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Penalty</span><span className="mono text-rose-600">− {money(previewClosePenalty())}</span></div>
            {previewSettleTotal() > 0 && (
              <div className="flex justify-between"><span className="text-slate-500">Loan settlement</span><span className="mono text-rose-600" data-testid="sd-close-settle-preview">− {money(previewSettleTotal())}</span></div>
            )}
            <div className="flex justify-between font-semibold border-t border-slate-200 pt-1 mt-1">
              <span>Payout to customer</span>
              <span className="mono text-emerald-700" data-testid="sd-close-payout-preview">{money(previewClosePayout())}</span>
            </div>
            {linkedLoans.some((l) => settlePick[l.id] && (l.outstanding > (acc.balance - previewClosePenalty() - previewSettleTotal() + (settlePick[l.id] ? 0 : 0)))) && (
              <div className="text-xs text-amber-700 pt-1">
                Note: If a loan's outstanding exceeds available balance, only the covered portion is settled and the rest stays as balance loan.
              </div>
            )}
          </div>

          {closeErr && <div className="text-sm text-rose-600">{String(closeErr)}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={() => setCloseOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={closing} data-testid="sd-confirm-close">
              {closing ? "Closing…" : "Confirm closure"}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal open={loanReqOpen} onClose={() => setLoanReqOpen(false)} title="Request loan against this deposit" maxWidth="max-w-lg">
        {loanReqDone ? (
          <div className="text-center py-6">
            <div className="text-emerald-700 heading text-lg font-semibold mb-2">Request submitted ✓</div>
            <div className="text-sm text-slate-600 mb-4">
              Admin has been notified. Track status in <b>Loan Requests</b>.
            </div>
            <Button onClick={() => setLoanReqOpen(false)}>Close</Button>
          </div>
        ) : (
          <form onSubmit={async (e) => {
            e.preventDefault();
            setLoanReqErr(""); setLoanReqSaving(true);
            try {
              await api.post("/loan-requests", {
                customer_id: acc.customer_id,
                savings_id: acc.id,
                amount: Number(loanReqForm.amount),
                tenure_days: Number(loanReqForm.tenure_days),
                interest_rate: Number(loanReqForm.interest_rate),
                interest_method: loanReqForm.interest_method,
                purpose: loanReqForm.purpose || null,
                notes: loanReqForm.notes || null,
              });
              setLoanReqDone(true);
            } catch (e2) {
              setLoanReqErr(formatApiError(e2.response?.data?.detail) || "Failed");
            } finally { setLoanReqSaving(false); }
          }} className="space-y-4">
            <div className="text-sm text-slate-600">
              <div><span className="text-slate-400">Deposit:</span> <span className="mono">{acc.product?.toUpperCase()}</span></div>
              <div><span className="text-slate-400">Available balance:</span> <span className="mono font-semibold text-emerald-700">{money(acc.balance || 0)}</span></div>
              <div className="text-xs text-slate-500 mt-1">Max loan is 90% of balance: <span className="mono">{money((acc.balance || 0) * 0.9)}</span></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Amount requested" type="number" step="0.01" required
                testId="lr-amount"
                value={loanReqForm.amount}
                onChange={(e) => setLoanReqForm({ ...loanReqForm, amount: e.target.value })} />
              <Input label="Tenure (days)" type="number" required
                testId="lr-tenure"
                value={loanReqForm.tenure_days}
                onChange={(e) => setLoanReqForm({ ...loanReqForm, tenure_days: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Interest rate %" type="number" step="0.01" required
                testId="lr-rate"
                value={loanReqForm.interest_rate}
                onChange={(e) => setLoanReqForm({ ...loanReqForm, interest_rate: e.target.value })} />
              <Select label="Interest method" testId="lr-method"
                value={loanReqForm.interest_method}
                onChange={(e) => setLoanReqForm({ ...loanReqForm, interest_method: e.target.value })}>
                <option value="SI">Simple Interest</option>
                <option value="CI_yearly">Compound · Yearly</option>
                <option value="CI_quarterly">Compound · Quarterly</option>
                <option value="CI_monthly">Compound · Monthly</option>
                <option value="CI_daily">Compound · Daily</option>
              </Select>
            </div>
            <Input label="Purpose (short)" testId="lr-purpose"
              value={loanReqForm.purpose}
              onChange={(e) => setLoanReqForm({ ...loanReqForm, purpose: e.target.value })} />
            <Input label="Notes (optional)" testId="lr-notes"
              value={loanReqForm.notes}
              onChange={(e) => setLoanReqForm({ ...loanReqForm, notes: e.target.value })} />
            {loanReqErr && <div className="text-sm text-rose-600">{loanReqErr}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" type="button" onClick={() => setLoanReqOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={loanReqSaving} data-testid="lr-submit">
                {loanReqSaving ? "Submitting…" : "Submit request"}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

function RenewModal({ open, onClose, account, onSubmit }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (!open || !account) return;
    setForm({
      tenure_months: account.tenure_months || "",
      interest_rate: account.interest_rate ?? "",
      interest_method: account.interest_method || "SI",
      principal: account.product === "fd" ? (account.maturity_value || account.balance || "") : "",
      installment_amount: account.installment_amount ?? "",
    });
  }, [open, account]);
  if (!account) return null;
  const submit = (e) => {
    e.preventDefault();
    const body = {};
    ["tenure_months", "interest_rate", "principal", "installment_amount"].forEach((k) => {
      if (form[k] !== "" && form[k] != null) body[k] = Number(form[k]);
    });
    if (form.interest_method) body.interest_method = form.interest_method;
    onSubmit(body);
  };
  return (
    <Modal open={open} onClose={onClose} title="Renew this deposit" maxWidth="max-w-lg">
      <form onSubmit={submit} className="space-y-4">
        <div className="text-xs text-slate-500">
          A new {account.product.toUpperCase()} account will be opened. The current one will be marked matured and linked to the renewal.
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Tenure (months)" type="number" required value={form.tenure_months || ""} onChange={(e) => setForm({ ...form, tenure_months: e.target.value })} testId="renew-tenure" />
          <Input label="Interest rate %" type="number" step="0.01" required value={form.interest_rate ?? ""} onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} testId="renew-rate" />
        </div>
        <Select label="Interest method" value={form.interest_method} onChange={(e) => setForm({ ...form, interest_method: e.target.value })} testId="renew-method">
          <option value="SI">Simple Interest</option>
          <option value="CI_yearly">Compound · Yearly</option>
          <option value="CI_quarterly">Compound · Quarterly</option>
          <option value="CI_monthly">Compound · Monthly</option>
          <option value="CI_daily">Compound · Daily</option>
        </Select>
        {account.product === "fd" && (
          <Input label="Principal (defaults to maturity value)" type="number" step="0.01" value={form.principal ?? ""} onChange={(e) => setForm({ ...form, principal: e.target.value })} testId="renew-principal" />
        )}
        {account.product === "rd" && (
          <Input label="Monthly installment" type="number" step="0.01" value={form.installment_amount ?? ""} onChange={(e) => setForm({ ...form, installment_amount: e.target.value })} testId="renew-installment" />
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" data-testid="confirm-renew-btn">Renew now</Button>
        </div>
      </form>
    </Modal>
  );
}
const productLabel = (p) => ({ daily: "Daily Savings", rd: "Recurring Deposit", fd: "Fixed Deposit" }[p] || p);
